import React, { FC, memo, useLayoutEffect, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { some } from 'lodash';
import { Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Event as StreamChatEvent } from 'stream-chat';
import {
  Channel as StreamChatChannel,
  MessageInput,
  MessageList,
  Window,
  useChatContext,
} from 'stream-chat-react';

import { ContentFragment, ReactionType, useAddReactionAnalyticsMutation } from 'api';
import { Events } from 'analytics';
import { useCurrentUser } from 'hooks';
import { Loaders } from 'components';
import { useAddComment } from 'features/content/updaters';

import EmptyStateList from './EmptyStateList';
import TypingIndicator from './TypingIndicator';
import MessageInputCustom from './MessageInputCustom';
import MessageCustom from './MessageCustom';

type Message = Record<'mentioned_users', Array<{ id: string }>>;
type TypedEvent = StreamChatEvent<
  Record<string, unknown>,
  Record<string, unknown>,
  '',
  Record<string, unknown>,
  Message,
  Record<string, unknown>,
  Record<string, unknown>
>;

interface IProps {
  id: ContentFragment['content_id'];
}

const useStyles = makeStyles(theme => ({
  empty: {
    marginTop: theme.spacing(2),
  },
  inlineChannel: {
    '@global': {
      '.str-chat-channel.messaging .str-chat__main-panel': {
        padding: 0,
        border: `1px solid ${theme.custom.palette.grey3}`,
        borderRadius: 4,
      },
      '.messaging.str-chat .str-chat__list': {
        padding: `${theme.spacing()}px ${theme.spacing(5)}px 0`,
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
      },
      '.str-chat__main-panel': {
        padding: `${theme.spacing(2)}px ${theme.spacing(2)}px 0 0`,
      },
      '.rfu-file-upload-button': {
        top: '50%',
        transform: 'translateY(-50%)',
      },
      '.str-chat__input-flat-emojiselect': {
        top: '50%',
        transform: 'translateY(-50%)',
      },
    },
  },
}));

const InlineChannel: FC<IProps> = ({ id }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const currentUser = useCurrentUser();

  const chatContext = useChatContext();
  const chatClient = chatContext.client;
  const chatChannel = chatClient.channel('comments', id);
  const commentedRef = useRef<boolean>(false);
  const addComment = useAddComment();

  const { mutate: addReactionAnalytics } = useAddReactionAnalyticsMutation();

  useLayoutEffect(() => {
    const headerEl = document.querySelector('header');
    const { offsetHeight } = headerEl;

    const channelEl: HTMLDivElement = document.querySelector('.str-chat-channel');
    channelEl.style.maxHeight = `calc(100vh - ${offsetHeight + theme.custom.mainOffset * 2}px`;
  }, []);

  useEffect(() => {
    function trackViewed() {
      if (!chatChannel.state) {
        return;
      }

      const { membership } = chatChannel.state;
      if (!membership?.role && !commentedRef.current) {
        window?.analytics?.track(Events.DiscussionViewed);
      }
    }

    function trackComment(event: TypedEvent) {
      const { user, message } = event;

      addComment();

      if (user.id !== currentUser.id) {
        return;
      }

      const mentionedIds = message.mentioned_users.map(mentioned => mentioned.id);
      if (mentionedIds.length) {
        void chatChannel.addMembers(mentionedIds);
      }

      window?.analytics?.track(Events.CommentUnique, {
        text_comment: !!message.text,
        image_comment: some(message.attachments, ['type', 'image']),
        url_comment: some(message.attachments, 'og_scrape_url'),
        file_comment: false,
        mention_comment: !!message.mentioned_users.length,
        reply_comment: false,
      });
    }

    chatChannel.on(event => {
      if (event.type === 'message.new') {
        handleReactionAnalytics();
        trackComment(event as TypedEvent);
      }
    });

    return () => {
      trackViewed();
      chatClient.off('message.new', trackComment);
    };
  }, [chatChannel]);

  useEffect(() => {
    function trackSubscribed() {
      window?.analytics?.track(Events.DiscussionSubscribed);
    }

    chatClient.on(event => {
      if (event.type === 'notification.added_to_channel') {
        trackSubscribed();
      }
    });
    return () => chatClient.off('notification.added_to_channel', trackSubscribed);
  }, [chatClient]);

  function handleReactionAnalytics() {
    addReactionAnalytics({
      content_id: id,
      reaction_type: ReactionType.Comment,
    });
  }

  function renderLoader() {
    return <Loaders.Standard />;
  }

  function renderEmptyMessageList() {
    return (
      <div className={classes.empty}>
        <Typography variant="h4" paragraph>
          {t('discussions:noMessagesTitle')}
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          {t('discussions:emptySubhead')}
        </Typography>
        <EmptyStateList />
      </div>
    );
  }

  return (
    <div className={classes.inlineChannel}>
      <StreamChatChannel channel={chatChannel} LoadingIndicator={renderLoader}>
        <Window>
          <MessageList
            Message={MessageCustom}
            EmptyStateIndicator={renderEmptyMessageList}
            TypingIndicator={TypingIndicator}
          />
          <MessageInput Input={MessageInputCustom} focus />
        </Window>
      </StreamChatChannel>
    </div>
  );
};

export default memo(InlineChannel);
